import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BlueprintBackground } from "@/components/BlueprintBackground";
import { Logo } from "@/components/Logo";
import { Wordmark } from "@/components/Wordmark";
import { api } from "@/api/client";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim(), origin_url: window.location.origin });
    } catch (e) {
      // intentionally ignored - always show the same neutral confirmation
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="forgot-password-page">
      <BlueprintBackground />
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Logo size={26} />
          <Wordmark className="font-heading text-xl font-bold tracking-tight text-white" />
        </Link>
        <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-md p-8 shadow-2xl">
          {submitted ? (
            <div className="text-center" data-testid="forgot-password-confirmation">
              <MailCheck className="text-blue-400 mx-auto mb-4" size={32} />
              <h1 className="font-heading text-xl font-bold text-white mb-2">Check your email</h1>
              <p className="text-slate-400 text-sm mb-6">If that email is registered, we've sent a link to reset your password.</p>
              <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium text-sm">Back to log in</Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-white mb-1">Forgot password</h1>
              <p className="text-slate-400 text-sm mb-6">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-password-email-input"
                    className="mt-1.5 bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500"
                    placeholder="you@company.com"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="forgot-password-submit-button"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  Send Reset Link
                </Button>
              </form>
              <p className="text-sm text-slate-400 mt-6 text-center">
                <Link to="/login" className="text-blue-400 hover:text-blue-300 font-medium" data-testid="forgot-password-login-link">
                  Back to log in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
